import { sortBy } from 'lodash/fp';
import { useSelector } from 'react-redux';
import { Field, getFormValues, InjectedFormProps, reduxForm } from 'redux-form';

import InputField from 'dashboard/components/FormControls/InputField';
import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import Form from 'dashboard/layouts/FormLayout/Form';
import displayBrokerType from 'dashboard/lib/valueDisplayers/reportFinraBrokerCheck';
import { FinraBrokerCheckBrokerType } from 'dashboard/models/report/finra-broker-check';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { US_STATES } from 'lib/constants/locales';
import PiiAttributes from 'lib/interfaces/PiiAttributes';
import { isMaxLength, isRequired, withMessage } from 'lib/utils/formValidation';

const US_STATE_OPTIONS = sortBy(
  'label',
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  Object.keys(US_STATES).map((code: keyof typeof US_STATES) => ({
    label: US_STATES[code],
    value: code,
  })),
);

const BROKER_TYPE_OPTIONS = Object.values(FinraBrokerCheckBrokerType).map((entityType) => ({
  label: displayBrokerType(entityType),
  value: entityType,
}));

const validate = {
  'reference-id': [
    withMessage('Please limit your Reference ID to 255 characters', isMaxLength(255)),
  ],
  'broker-type': [withMessage('Please select a broker type', isRequired)],
};

interface FormCreateReportFinraBrokerCheckProps {
  hideReferenceId: boolean;
  handleCancel: () => void;
  readOnly?: boolean;
}

const FormCreateReportFinraBrokerCheck = ({
  submitting,
  hideReferenceId,
  handleSubmit,
  handleCancel,
  readOnly,
}: FormCreateReportFinraBrokerCheckProps &
  InjectedFormProps<
    FormCreateReportFinraBrokerCheckData,
    FormCreateReportFinraBrokerCheckProps
  >) => {
  const formValues: FormCreateReportFinraBrokerCheckData = useSelector(
    getFormValues(FORM_CREATE_REPORT_FINRA_BROKER_CHECK_NAME),
  );

  const brokerTypeIsIndividual =
    formValues?.['broker-type'] === FinraBrokerCheckBrokerType.Individual;
  const stateIsSelected = formValues?.['address-subdivision'] != null;

  return (
    <Form onSubmit={handleSubmit}>
      {!hideReferenceId && (
        <Field
          id='create-report-finra-broker-check__reference-id'
          label='Reference ID (Optional)'
          name='reference-id'
          disabled={submitting || readOnly}
          component={InputField}
          validate={validate['reference-id']}
          data-test='create-report-finra-broker-check__reference-id'
        />
      )}
      <Field
        id='create-report-finra-broker-check__broker-type'
        label='Broker type'
        component={ReactSelectField}
        name='broker-type'
        data-test='create-report-finra-broker-check__broker-type'
        disabled={submitting || readOnly}
        placeholder='Select Broker Type...'
        options={BROKER_TYPE_OPTIONS}
        validate={validate['broker-type']}
        portal={true}
      />

      <Field
        id='create-report-finra-broker-check__crd-number'
        label='CRD# (Optional)'
        name='crd-number'
        disabled={submitting || readOnly}
        component={InputField}
        placeholder='CRD#'
        data-test='create-report-finra-broker-check__crd-number'
      />

      {brokerTypeIsIndividual && (
        <Field
          id='create-report-finra-broker-check__name-full'
          label='Full name (Optional)'
          name={PiiAttributes.NameFull}
          disabled={submitting || readOnly}
          component={InputField}
          placeholder='Full name'
          data-test='create-report-finra-broker-check__name-full'
        />
      )}

      <Field
        id='create-report-finra-broker-check__firm-name'
        label='Firm (Optional)'
        name='firm-name'
        disabled={submitting || readOnly}
        component={InputField}
        placeholder='Firm name'
        data-test='create-report-finra-broker-check__firm-name'
      />

      <Field
        id='create-report-finra-broker-check__address-subdivision'
        label='State (Optional)'
        name='address-subdivision'
        disabled={submitting || readOnly}
        component={ReactSelectField}
        placeholder='State'
        options={US_STATE_OPTIONS}
        data-test='create-report-finra-broker-check__address-subdivision'
        portal={true}
      />

      {stateIsSelected && (
        <Field
          id='create-report-finra-broker-check__address-city'
          label='City (Optional)'
          name='address-city'
          disabled={submitting || readOnly}
          component={InputField}
          placeholder='City'
          data-test='create-report-finra-broker-check__address-city'
        />
      )}

      <HStack equal={true}>
        <Button
          variant='basic'
          id='create-report-finra-broker-check__cancel'
          data-test='create-report-finra-broker-check__cancel'
          disabled={submitting}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          data-test='create-report-finra-broker-check__submit'
          disabled={submitting}
          isPending={submitting}
        >
          Create Report
        </Button>
      </HStack>
    </Form>
  );
};

export interface FormCreateReportFinraBrokerCheckData {
  'reference-id'?: string;
  'broker-type'?: FinraBrokerCheckBrokerType;
  'crd-number'?: string;
  'firm-name'?: string;
  [PiiAttributes.NameFull]?: string;
  [PiiAttributes.AddressCity]?: string;
  [PiiAttributes.AddressSubdivision]?: string;
}

export const FORM_CREATE_REPORT_FINRA_BROKER_CHECK_NAME = 'formCreateReportFinraBrokerCheckData';

export default reduxForm<
  FormCreateReportFinraBrokerCheckData,
  FormCreateReportFinraBrokerCheckProps
>({
  form: FORM_CREATE_REPORT_FINRA_BROKER_CHECK_NAME,
})(FormCreateReportFinraBrokerCheck);
